import React from 'react';
import { forkJoin, of, concat } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  getAllQuestions$,
  saveAnswers$,
  changePassword$,
  getPwdComplexityRequirements$
} from 'api/securityQuestions/service';
import {
  sendActivationCode$,
  validateActivationCode$,
  updatesSecurityContactInfo$,
  updateFirstTimeLoginStatus$,
  fetchUserData
} from 'api/user/service';
import {
  contactTypes,
  SUBMIT_TYPES,
  FIELD_NAMES,
  VIEW_TYPES,
  STORAGE_ITEMS,
  CONTACT_POINTS,
  STEPS
} from 'components/securityContact/securityContacts.constants';
import { API_ERROR, WORKSPACES } from 'constants.js';

const handleError = ({
  callback,
  data,
  ignoreSetMessage = false,
  locale,
  setChallengeManager,
  setMessage
}) => {
  if (!data) return;
  if (data.challeangeable === true || data?.[0]?.code === API_ERROR.ACCOUNT_LOCKED) {
    setChallengeManager({
      data,
      callback: () => callback()
    });
  } else if (!ignoreSetMessage) {
    setMessage({
      title: data && data[0] ? data[0].userMessage || data[0].code : locale['common.error']
    });
  }
};

const runOnSubmit = ({
  contactPoint,
  history,
  locale,
  logout,
  onChange,
  setChallengeManager,
  setConfirmation,
  setContactPoint,
  setCurrentUser,
  setMessage,
  submitType,
  user,
  values
}) => {
  const securityValues = values.security || {};
  if (submitType === SUBMIT_TYPES.SECURITY_QUESTIONS) {
    // Set session storage to save state
    window.sessionStorage.setItem(
      STORAGE_ITEMS.FIRST_TIME_SETUP,
      JSON.stringify({ security: values.security, contactPoint })
    );
    // If user is not mfa enabled then we skip this step
    if (user.isMfaEnabled) {
      return history.push(`${WORKSPACES.DASHBOARD}${SUBMIT_TYPES.SECURITY_CONTACTS}`);
    }
    return history.push(`${WORKSPACES.DASHBOARD}${SUBMIT_TYPES.NEW_PASSWORD}`);
  }
  if (submitType === SUBMIT_TYPES.SECURITY_CONTACTS) {
    setContactPoint((state) => ({ ...state, completed: true }));
    // Set session storage to save state
    window.sessionStorage.setItem(
      STORAGE_ITEMS.FIRST_TIME_SETUP,
      JSON.stringify({ security: values.security, contactPoint })
    );
    return history.push(`${WORKSPACES.DASHBOARD}${SUBMIT_TYPES.NEW_PASSWORD}`);
  }
  if (submitType === SUBMIT_TYPES.SEND_CODE) {
    setContactPoint((state) => ({
      ...state,
      sendingCode: true,
      verifyingCodeError: null,
      error: null
    }));
    onChange(FIELD_NAMES.SECURITY, { ...securityValues, activationCode: '' });
    const execute = () =>
      sendActivationCode$([
        {
          contactType: contactTypes.sms,
          default: securityValues.makeDefault,
          value: securityValues.smsNumber.replace(/\D/g, '') || securityValues.phoneNumber
        }
      ]).subscribe(
        ({ data, success }) => {
          if (!success) {
            handleError({
              locale,
              data,
              setChallengeManager,
              callback: () => execute(),
              setMessage
            });
          }
          if (success) {
            return setContactPoint((state) => ({
              ...state,
              sendingCode: false,
              verifyingCode: true
            }));
          }
          // TODO: handle if API error
          return setContactPoint((state) => ({
            ...state,
            sendingCode: false,
            verifyingCode: false
          }));
          // TODO: handle if server (404, 500, etc…) error
        },
        () => null
      );
    return execute();
  }
  if (submitType === SUBMIT_TYPES.VERIFY_CODE) {
    setContactPoint((state) => ({ ...state, checkingCode: true, error: null }));
    const execute = () =>
      validateActivationCode$(securityValues.activationCode).subscribe(
        ({ data, success }) => {
          if (!success) {
            handleError({
              locale,
              data,
              setChallengeManager,
              callback: () => execute(),
              setMessage,
              ignoreSetMessage: true
            });
          }
          if (success) {
            if (
              user.isEmailOTPDisallowed &&
              !securityValues.makeDefault &&
              !securityValues.makeVoiceDefault
            ) {
              onChange(FIELD_NAMES.SECURITY, { ...securityValues, makeDefault: true });
            }
            return setContactPoint((state) => ({
              ...state,
              checkingCode: false,
              contactPointReadyToBeAdded: true,
              verifyingCodeError: false
            }));
          }
          // TODO: handle if API error
          return setContactPoint((state) => ({
            ...state,
            checkingCode: false,
            verifyingCodeError: true
          }));
          // TODO: handle if server (404, 500, etc…) error
        },
        () => null
      );
    return execute();
  }
  if (submitType === SUBMIT_TYPES.REMOVE_MOBILE_CONTACT) {
    // Make sure the user always has at least one contact default point
    onChange(FIELD_NAMES.SECURITY, {
      ...securityValues,
      smsNumber: null,
      makeDefault: null,
      activationCode: null,
      mobileContact: false,
      includeEmail: !user.isEmailOTPDisallowed
    });
    setContactPoint(() => ({ sendingCode: false, error: null }));
  }
  if (submitType === SUBMIT_TYPES.LOGOUT) {
    return setConfirmation({
      component: () => (
        <div>
          <div className="form-group">
            <p>{locale['profile.navigateAway']}</p>
            <strong>{locale['profile.continue']}</strong>
          </div>
          <div className="form-group">
            <button
              className="btn btn-primary"
              onClick={() => {
                window.sessionStorage.removeItem(STORAGE_ITEMS.FIRST_TIME_SETUP);
                logout();
              }}
            >
              {locale['common.continue']}
            </button>
            <button className="btn btn-link" onClick={() => setConfirmation(null)}>
              {locale['common.cancel']}
            </button>
          </div>
        </div>
      )
    });
  }
  if (submitType === SUBMIT_TYPES.COMPLETE) {
    onChange(FIELD_NAMES.IS_SUBMITTING, true);
    const apiSuccess = {
      password: false,
      question: false,
      contact: !user.isMfaEnabled
    };
    const saveQuestionsAndAnswers = () =>
      saveAnswers$({
        answer1: securityValues.securityQuestionAnswer1,
        answer2: securityValues.securityQuestionAnswer2,
        answer3: securityValues.securityQuestionAnswer3,
        question1Id: securityValues?.securityQuestion1?.[0]?.id,
        question2Id: securityValues?.securityQuestion2?.[0]?.id,
        question3Id: securityValues?.securityQuestion3?.[0]?.id
      }).pipe(
        map(({ data, success }) => {
          if (success) apiSuccess.question = true;
          return { data, success, name: STEPS.QUESTIONS };
        })
      );
    const updateSecurityContactInfo = () =>
      updatesSecurityContactInfo$({
        securityContacts: [
          ...(securityValues.voiceContact
            ? [
                {
                  contactType: contactTypes.voice,
                  default: securityValues.makeVoiceDefault,
                  value: securityValues.voiceNumber,
                  extension: securityValues.extension || null,
                  vruDelay:
                    securityValues.extension &&
                    Array.isArray(securityValues.vruDelay) &&
                    securityValues.vruDelay
                      ? securityValues.vruDelay[0].id
                      : null
                }
              ]
            : []),
          ...(securityValues.mobileContact
            ? [
                {
                  contactType: contactTypes.sms,
                  default: securityValues.makeDefault,
                  value: securityValues.smsNumber.replace(/\D/g, '')
                }
              ]
            : []),
          ...(securityValues.includeEmail
            ? [
                {
                  contactType: contactTypes.email,
                  default: !securityValues.makeDefault && !securityValues.makeVoiceDefault,
                  value: user?.userInformation?.email
                }
              ]
            : [])
        ],
        viewType: VIEW_TYPES.ENROLL
      }).pipe(
        map(({ data, success }) => {
          if (success) apiSuccess.contact = true;
          return { data, success, name: STEPS.CONTACT };
        })
      );
    const changePassword = () =>
      changePassword$({
        newPassword: securityValues.password,
        oldPassword:
          user.tempPassword || window.sessionStorage.getItem(STORAGE_ITEMS.TEMP_PASSWORD),
        redirectToken: sessionStorage.getItem(STORAGE_ITEMS.REDIRECT_TOKEN)
      }).pipe(
        map(({ data, success }) => {
          if (success) apiSuccess.password = true;
          return { data, success, name: STEPS.PASSWORD };
        })
      );
    return concat(
      saveQuestionsAndAnswers(),
      // Only save contact info if user isMfaEnabled
      user.isMfaEnabled
        ? updateSecurityContactInfo()
        : of({ data: [], success: true, name: STEPS.CONTACT }),
      changePassword()
    ).subscribe((action) => {
      const { data, success } = action;
      if (!success) {
        handleError({
          locale,
          data,
          setChallengeManager,
          // TODO: test more challenge manager scenarios
          // NOTE: Challenge shouldn't happen for first time users
          callback: () =>
            // if (name === 'questions') return () => saveQuestionsAndAnswers().subscribe();
            // if (name === 'contact') return () => updateSecurityContactInfo().subscribe();
            // if (name === 'password') return () => changePassword().subscribe();
            null,
          setMessage
        });
      }
      if (apiSuccess.password && apiSuccess.question && apiSuccess.contact) {
        updateFirstTimeLoginStatus$().subscribe(async () => {
          const currentUser = await fetchUserData();
          setCurrentUser(currentUser);
          history.push('/');
          // TODO: Add snackbar success message
          // TODO: server error
        });
      }
    });
  }
  return null;
  // TODO: on success update user with setCurrentUser (redux)
};

// Function that returns a useEffect function to load data and state
export const loadData =
  ({ onChange, setAPIData, setAPIErrors, setContactPoint, setLoadedTheme, user }) =>
  () => {
    const observable$ = forkJoin({
      questions: getAllQuestions$(),
      passwordRequirements: getPwdComplexityRequirements$()
    });
    const subscriber$ = observable$.subscribe(
      ({ passwordRequirements, questions }) => {
        const previousState = window.sessionStorage.getItem(STORAGE_ITEMS.FIRST_TIME_SETUP);
        const previousSecurityState = previousState ? JSON.parse(previousState).security : null;
        const previousContactPointState = previousState
          ? JSON.parse(previousState).contactPoint
          : null;
        // If success is false on any of these guys show an error message
        if (!questions.success || !passwordRequirements.success) setAPIErrors(true);
        // TODO: Use sessionStorage if available to reload state and useState hooks outside of useEffect (glu & contactPoint)
        setAPIData({
          questions: questions.data.securityQuestions.map((question) => ({
            ...question,
            id: question.questionId,
            name: question.question
          })),
          passwordRequirements: passwordRequirements.data
        });
        // Set default glu state for when they don't have answered questions
        if (!previousState) {
          // Pre-populate this with SMS value
          // TODO: these onChanges do not seem to be doing anything since updating glu/validation-react
          // TODO: replace withFormProvider with useValidation hook and initialData
          onChange(FIELD_NAMES.SECURITY, {
            contactPoint: [CONTACT_POINTS.SMS],
            includeEmail: true
          });
        }
        if (previousState) {
          onChange(FIELD_NAMES.SECURITY, previousSecurityState);
          setContactPoint(previousContactPointState);
        }
        if (user.tempPassword)
          window.sessionStorage.setItem(STORAGE_ITEMS.TEMP_PASSWORD, user.tempPassword);
        setTimeout(() => setLoadedTheme(true), 500);
        // If there is a server error (500, 404, etc…) then show an error message
      },
      () => setAPIErrors(true)
    );
    // TODO: If user is does not have isMFAEnabled then hide security contacts section
    return () => {
      subscriber$.unsubscribe();
    };
  };

export default runOnSubmit;
